/**
 * Mech lifecycle and plain mech fields — create / delete / restore, rename, image, notes, and the
 * active-mech pointer.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { resolveActiveMechId } from "../activeMech";
import type { RosterLeafCommand } from "../commands";
import { CHOSEN_DAMAGE_TYPES, PROTOTYPE_REVISIONS, PROTOTYPE_WEAPON_TYPES, type MechData, type PilotData, type RosterState } from "../types";
import { changed, requireMech, requirePilot, withMech, withPilot, type RosterApplyResult } from "./context";

export function applyCreateMech(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "create_mech" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (pilot.mechs.some((m) => m.id === cmd.mechId)) {
    return err(new CommandError(`create_mech: mech ${cmd.mechId} already exists`));
  }
  const mech: MechData = {
    id: cmd.mechId,
    name: cmd.name,
    frameId: null,
    loadouts: [{ id: cmd.loadoutId, name: "Primary", mounts: [], systems: [] }],
    activeLoadoutIndex: 0,
  };
  const next: PilotData = { ...pilot, mechs: [...pilot.mechs, mech] };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "delete_mech",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
    },
    events: changed(cmd.pilotId),
  });
}

export function applyDeleteMech(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "delete_mech" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const next: PilotData = {
    ...pilot,
    mechs: pilot.mechs.filter((m) => m.id !== cmd.mechId),
  };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "restore_mech",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mech,
    },
    events: changed(cmd.pilotId),
  });
}

export function applyRestoreMech(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "restore_mech" }>,
): Result<RosterApplyResult, CommandError> {
  // Internal inverse of delete_mech: splice the captured raw mech back in.
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (pilot.mechs.some((m) => m.id === cmd.mech.id)) {
    return err(new CommandError(`restore_mech: mech ${cmd.mech.id} already exists`));
  }
  const next: PilotData = { ...pilot, mechs: [...pilot.mechs, cmd.mech] };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "delete_mech",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mech.id,
    },
    events: changed(cmd.pilotId),
  });
}

export function applyRenameMech(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "rename_mech" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const prev = mech.name;
  const nextMech: MechData = { ...mech, name: cmd.name };
  return ok({
    state: withPilot(state, withMech(pilot, nextMech)),
    inverse: {
      type: "rename_mech",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      name: prev,
    },
    events: changed(cmd.pilotId),
  });
}

export function applySetMechImage(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_mech_image" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  // Optional field leaves restore as "" rather than widening the command contract
  // (mirrors set_pilot_text); both forms render as unset.
  const prev = mech.image ?? "";
  const nextMech: MechData = { ...mech, image: cmd.image };
  return ok({
    state: withPilot(state, withMech(pilot, nextMech)),
    inverse: {
      type: "set_mech_image",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      image: prev,
    },
    events: changed(cmd.pilotId),
  });
}

export function applySetMechPrototype(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_mech_prototype" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const revisions = cmd.prototype?.revisions;
  if (revisions !== undefined && (revisions.length !== 2 || revisions[0] === revisions[1] || !revisions.every((r) => PROTOTYPE_REVISIONS.includes(r)))) {
    return err(new CommandError(`set_mech_prototype: choose two different Revisions from ${PROTOTYPE_REVISIONS.join(", ")} (got ${revisions.join(", ") || "none"})`));
  }
  const weaponType = cmd.prototype?.weaponType;
  if (weaponType !== undefined && !PROTOTYPE_WEAPON_TYPES.includes(weaponType)) {
    return err(new CommandError(`set_mech_prototype: weapon type must be one of ${PROTOTYPE_WEAPON_TYPES.join(", ")} (got ${weaponType})`));
  }
  const damageType = cmd.prototype?.damageType;
  if (damageType !== undefined && !CHOSEN_DAMAGE_TYPES.includes(damageType)) {
    return err(new CommandError(`set_mech_prototype: damage type must be one of ${CHOSEN_DAMAGE_TYPES.join(", ")} (got ${damageType})`));
  }
  const uses = cmd.prototype?.limitedUses;
  if (uses !== undefined && !(Number.isInteger(uses) && uses >= 1)) {
    return err(new CommandError(`set_mech_prototype: Limited uses must be a whole number ≥ 1 (got ${uses})`));
  }
  const nextMech: MechData = { ...mech };
  if (cmd.prototype === undefined) delete nextMech.prototype;
  else nextMech.prototype = cmd.prototype;
  return ok({
    state: withPilot(state, withMech(pilot, nextMech)),
    inverse: { type: "set_mech_prototype", actorId: cmd.actorId, pilotId: cmd.pilotId, mechId: cmd.mechId, ...(mech.prototype !== undefined ? { prototype: mech.prototype } : {}) },
    events: changed(cmd.pilotId),
  });
}

export function applySetMechNotes(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_mech_notes" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  // Optional field leaves restore as "" rather than widening the command contract
  // (mirrors set_mech_image / set_pilot_text); both forms render as unset.
  const prev = mech.notes ?? "";
  const nextMech: MechData = { ...mech, notes: cmd.notes };
  return ok({
    state: withPilot(state, withMech(pilot, nextMech)),
    inverse: {
      type: "set_mech_notes",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      notes: prev,
    },
    events: changed(cmd.pilotId),
  });
}

export function applySetActiveMech(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_active_mech" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  // Inverse targets the currently-RESOLVED active mech (the fallback may be implicit), so undo
  // lands on the same mech the user saw active. A mechless pilot can't reach here (requireMech).
  const prevActive = resolveActiveMechId(pilot) ?? cmd.mechId;
  const next: PilotData = { ...pilot, activeMechId: cmd.mechId };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_active_mech",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: prevActive,
    },
    events: changed(cmd.pilotId),
  });
}
